import { Project } from '../types';

export const projectsData: Project[] = [
  {
    id: "option-chain-analyzer",
    title: "Option Chain Analyzer",
    description: "Designed and built end-to-end: Java backend ingesting live Intrinio market data, React frontend with interactive tables and charting, WebSocket streaming for real-time updates, and multi-format export (CSV, JSON, PDF).",
    type: "web",
    year: 2025,
    image: "/assets/images/realtimeoptions.jpeg",
    featured: true,
    category: "FinTech",
    technologies: ["Java", "Node.js", "Express", "React", "Vite", "Socket.IO", "TanStack Table", "Recharts", "Intrinio API", "Maven", "PDFKit"],
    complexity: "High",
    impact: "Financial Analysis Tool",
    status: "In Development",
    outcome: "Real-time options analysis processing 10K+ contracts with sub-second WebSocket streaming",
    role: "Sole Engineer",
    highlights: ["Live Intrinio market data ingestion", "WebSocket real-time streaming", "Multi-format export (CSV, JSON, PDF)"],
    verificationBadges: ["WebSocket Streaming", "Real-time Data", "Multi-format Export"]
  },
  {
    id: "brain-2026",
    title: "BRAIN 2026 — ML Protein Prediction Platform",
    description: "Evolved from hand-coded C++ neural networks (2004) to production PyTorch platform with MLflow experiment tracking, Optuna hyperparameter optimization, Captum interpretability, and comprehensive test suite.",
    type: "research",
    year: 2026,
    image: "/assets/images/ANN.png",
    featured: true,
    category: "Bioinformatics",
    technologies: ["Python", "PyTorch", "FastAPI", "MLflow", "Optuna", "Captum", "ChromaDB", "Docker", "LoRA", "MLX", "ESM-2", "BioPython"],
    complexity: "Very High",
    impact: "Research Platform",
    status: "In Development",
    outcome: "22-year research arc: 5 neural architectures, 823 tests, LoRA fine-tuning on ESM-2 protein models",
    role: "Sole Researcher & Engineer",
    highlights: ["5 distinct neural architectures", "823 automated tests", "LoRA fine-tuning on ESM-2"],
    verificationBadges: ["823 Tests", "5 Architectures", "22-Year Evolution"]
  },
  {
    id: "rl-workbench",
    title: "RL Workbench",
    description: "Built a 3-phase RL post-training workbench: Reward Lab for designing and A/B testing reward functions across datasets, Playground for real TRL-powered GRPO/DPO training with live SSE metric streaming on Apple Silicon or CUDA, and Arena for head-to-head framework benchmarking with GPU passthrough in CUDA-capable Docker containers.",
    type: "web",
    year: 2026,
    image: "/assets/images/rlworkbench.jpeg",
    github: "https://github.com/ovai-felix/RL",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "FastAPI", "Next.js", "Tailwind CSS", "Docker", "PyTorch", "TRL", "Ollama", "SSE", "WebSocket", "SQLite", "Monaco Editor", "Recharts"],
    complexity: "Very High",
    impact: "AI Training Platform",
    status: "In Development",
    outcome: "12-algorithm RL workbench with real TRL training, Ollama-powered inference, and Docker-isolated framework benchmarking across TRL, VeRL, OpenRLHF, and NeMo RL",
    role: "Sole Engineer",
    highlights: ["12 RL algorithms (GRPO, DPO, PPO, REINFORCE++, etc.)", "Real TRL training on Apple Silicon (MPS) & CUDA", "Ollama integration for local inference", "GPU-passthrough Docker benchmarking in Arena"],
    verificationBadges: ["12 Algorithms", "Real TRL Training", "Apple Silicon Native", "Docker Orchestration"]
  },
  {
    id: "agentops",
    title: "AgentOps — Autonomous MLOps Platform",
    description: "Built an autonomous MLOps platform with a three-agent pipeline: Monitor Agent queries Snowflake for model metrics and drift, Investigator Agent searches RAG-based runbooks to diagnose root causes, and Remediator Agent executes fixes via Slack and GitHub — all with human approval gates for high-risk actions.",
    type: "web",
    year: 2026,
    image: "/assets/images/agentops.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "CrewAI", "Claude", "GPT-4o", "Snowflake", "FastAPI", "React", "Vite", "Tailwind CSS", "SSE", "RAG", "Composio", "Slack API", "GitHub API"],
    complexity: "Very High",
    impact: "MLOps Automation",
    status: "Completed",
    outcome: "Three-agent MLOps pipeline: alert to resolution in minutes with Snowflake metrics, RAG diagnosis, and Composio-powered remediation",
    role: "Sole Engineer",
    highlights: ["3-agent CrewAI pipeline (Monitor, Investigator, Remediator)", "Snowflake metrics + RAG-based diagnosis", "Human approval gates for high-risk actions", "SSE real-time streaming dashboard"],
    verificationBadges: ["3-Agent Pipeline", "Snowflake Integration", "Human-in-the-Loop"]
  },
  {
    id: "rag-system",
    title: "RAG System — Hybrid Retrieval with Citations",
    description: "Built a production-quality RAG system with hybrid retrieval combining ChromaDB vector search and BM25 keyword search, Reciprocal Rank Fusion, cross-encoder re-ranking, and grounded generation with inline citations — served via FastAPI with JWT auth and a Streamlit chat UI.",
    type: "web",
    year: 2026,
    image: "/assets/images/ragsystem.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "FastAPI", "ChromaDB", "BM25", "Cross-encoder", "OpenAI API", "Anthropic API", "Streamlit", "Docker", "JWT", "bcrypt"],
    complexity: "High",
    impact: "AI Retrieval Platform",
    status: "Completed",
    outcome: "Hybrid retrieval RAG system with vector + BM25 search, cross-encoder re-ranking, and citation-grounded generation",
    role: "Sole Engineer",
    highlights: ["Hybrid retrieval (vector + BM25 + RRF)", "Cross-encoder re-ranking", "Citation-grounded generation", "JWT-authenticated API"],
    verificationBadges: ["Hybrid Retrieval", "Cross-encoder", "Citation Generation"]
  },
  {
    id: "fine-tuned-model",
    title: "Fine-Tuned Sentiment Model — DeBERTa vs. Mistral 7B",
    description: "Fine-tuned DeBERTa-v3-base (full) and Mistral 7B (QLoRA 4-bit) for 5-class sentiment analysis on 25K Yelp reviews, then rigorously benchmarked against zero-shot, few-shot, and Claude Sonnet 4 prompting baselines — with ablation studies on data size, LoRA rank, and truncation.",
    type: "research",
    year: 2026,
    image: "/assets/images/modeltuning.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "PyTorch", "Hugging Face", "DeBERTa-v3", "Mistral 7B", "QLoRA", "ONNX", "FastAPI", "Claude Sonnet 4", "MLflow"],
    complexity: "Very High",
    impact: "ML Research",
    status: "Completed",
    outcome: "Head-to-head fine-tuning vs. prompting comparison: DeBERTa encoder, Mistral 7B QLoRA, and Claude Sonnet 4 on 5-class sentiment",
    role: "Sole Researcher & Engineer",
    highlights: ["DeBERTa (full fine-tune) vs. Mistral 7B (QLoRA)", "4-way comparison including Claude Sonnet 4", "Ablation studies (data size, LoRA rank, truncation)", "FastAPI serving with GPU + CPU/ONNX support"],
    verificationBadges: ["4-Way Comparison", "QLoRA Fine-tuning", "Ablation Studies"]
  },
  {
    id: "agentic-workflow",
    title: "Agentic Workflow — Multi-Step AI Agent",
    description: "Built a general-purpose AI agent with LangGraph performing multi-step reasoning, dynamic tool selection, structured outputs, and human-in-the-loop clarification — with a head-to-head comparison across Claude Sonnet 4, GPT-4o, and Llama 3 on GAIA benchmarks.",
    type: "web",
    year: 2026,
    image: "/assets/images/agenticworkflow.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "LangGraph", "Claude Sonnet 4", "GPT-4o", "Llama 3", "Ollama", "Tavily", "FastAPI", "Streamlit", "Redis", "Docker"],
    complexity: "Very High",
    impact: "AI Agent Platform",
    status: "Completed",
    outcome: "Multi-step AI agent with 3 LLM backends, 5-step reasoning cap, tool-use orchestration, and GAIA benchmark evaluation",
    role: "Sole Engineer",
    highlights: ["LangGraph state machine (Planner → Executor → Reflector → Synthesizer)", "3 LLM backends (Claude, GPT-4o, Llama 3)", "GAIA benchmark + custom evaluation", "Redis session persistence with trace visualization"],
    verificationBadges: ["3 LLM Backends", "GAIA Benchmark", "Human-in-the-Loop"]
  },
  {
    id: "inference-pipeline",
    title: "High-Performance Inference Pipeline",
    description: "Deployed Llama 3 8B on a consumer RTX 3080 (10GB VRAM) with vLLM, systematically benchmarking AWQ, GPTQ, and FP8 quantization across chat, batch, and code generation workloads at 1–8 concurrent users — with Prometheus + Grafana observability.",
    type: "research",
    year: 2026,
    image: "/assets/images/infpipeline.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "vLLM", "Llama 3 8B", "AWQ", "GPTQ", "FP8", "Locust", "Prometheus", "Grafana", "Docker", "CUDA"],
    complexity: "Very High",
    impact: "Inference Optimization",
    status: "Completed",
    outcome: "Systematic quantization benchmark: AWQ vs. GPTQ vs. FP8 on consumer GPU with latency, throughput, VRAM, and quality trade-off analysis",
    role: "Sole Engineer",
    highlights: ["3 quantization methods (AWQ, GPTQ, FP8) benchmarked", "3 workload profiles (chat, batch, code gen)", "Consumer-grade RTX 3080 — $0 cloud spend", "Prometheus + Grafana observability"],
    verificationBadges: ["3 Quantization Methods", "Consumer GPU", "Zero Cloud Cost"]
  },
  {
    id: "ml-monitoring",
    title: "Production ML System with Monitoring",
    description: "Built a complete ML system spanning classification (Transformer encoder), time-series forecasting (LSTM), and anomaly detection (Isolation Forest) — with Evidently AI drift detection, Grafana dashboards, multi-channel alerting, drift-triggered retraining, and blue-green deployment with auto-rollback.",
    type: "web",
    year: 2026,
    image: "/assets/images/mlmonitoring.png",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "PyTorch", "FastAPI", "MLflow", "Evidently AI", "Prometheus", "Grafana", "Docker Compose", "Slack", "LSTM", "Transformer", "Isolation Forest"],
    complexity: "Very High",
    impact: "ML Production Platform",
    status: "Completed",
    outcome: "Full-lifecycle ML system: 3 models, drift detection, automated retraining, blue-green deployment with 30-minute auto-rollback",
    role: "Sole Engineer",
    highlights: ["3 models (Transformer, LSTM, Isolation Forest)", "Evidently AI drift detection + automated retraining", "Blue-green deployment with 30-min auto-rollback", "4 role-specific Grafana dashboards"],
    verificationBadges: ["3 Model Types", "Drift Detection", "Blue-Green Deploy"]
  },
  {
    id: "ai-eval",
    title: "aeval — AI Evaluation Pipeline",
    description: "Built a pytest-style evaluation framework for local AI models — write eval cases declaratively, run against Ollama models, get confidence intervals and statistical comparisons with SciPy-powered analysis.",
    type: "web",
    year: 2025,
    image: "/assets/images/aieval.jpeg",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "FastAPI", "Next.js", "TimescaleDB", "Redis", "Ollama", "SciPy", "Click", "Docker"],
    complexity: "Very High",
    impact: "AI Evaluation Platform",
    status: "In Development",
    outcome: "Local-first AI eval system with statistical rigor — confidence intervals, multi-model comparison",
    role: "Sole Engineer",
    highlights: ["Pytest-style eval framework", "SciPy statistical analysis", "Multi-model comparison"],
    verificationBadges: ["Statistical Rigor", "CI Pipeline", "Multi-model"]
  },
  {
    id: "authz-platform",
    title: "Authorization Platform",
    description: "Designed multi-tenant authorization with gRPC policy evaluation, AI-powered policy generation via Anthropic API, immutable audit ledger on Redpanda, and Kong API gateway — spanning TypeScript, Go, and Python services.",
    type: "web",
    year: 2025,
    image: "/assets/images/authnauthn.jpeg",
    featured: true,
    category: "Security",
    technologies: ["TypeScript", "Go", "Fastify", "gRPC", "Next.js", "PostgreSQL", "Redis", "Redpanda", "Kong", "Python", "FastAPI", "Anthropic API"],
    complexity: "Very High",
    impact: "Enterprise Security Platform",
    status: "In Development",
    outcome: "Enterprise authz platform: RBAC/ABAC policy engine designed for sub-15ms P99 evaluation latency",
    role: "Architect & Sole Engineer",
    highlights: ["6 microservices across 3 languages", "gRPC policy evaluation", "AI-powered policy generation"],
    verificationBadges: ["6 Services", "Sub-15ms Design Target", "RBAC + ABAC"]
  },
  {
    id: "ai-product-os",
    title: "AI Product OS",
    description: "Created a comprehensive meta-framework for building AI products end-to-end — 12 interconnected frameworks spanning corporate strategy through crisis management, with sequenced order of operations and decision trees.",
    type: "consulting",
    year: 2025,
    image: "/assets/images/aiproductos.jpeg",
    featured: true,
    category: "Product Strategy",
    technologies: ["Product Management", "AI Strategy", "RICE Framework", "BOMI Loop", "HEART Metrics", "GTM Strategy", "Pricing Models", "A/B Testing"],
    complexity: "Very High",
    impact: "Strategic Framework",
    status: "Completed",
    outcome: "12 interconnected product frameworks with sequenced order of operations for AI product development",
    role: "Product Strategist",
    highlights: ["12 interconnected frameworks", "End-to-end product lifecycle", "Sequenced order of operations"],
    verificationBadges: ["12 Frameworks", "End-to-End Lifecycle", "Completed"]
  },
  {
    id: "openai-agentic-arch",
    title: "Open Source Agentic Architecture",
    description: "Designed an architectural framework for building Claude Code/Codex-style agentic environments on fine-tuned open source models — covering tool-use, extended thinking, safety sandboxing, and multi-model routing.",
    type: "research",
    year: 2025,
    image: "/assets/images/agentic.jpeg",
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "Ollama", "MLX", "GGUF", "FastAPI", "Llama 3.1", "Mistral", "DeepSeek", "Docker", "nsjail"],
    complexity: "Very High",
    impact: "Research Framework",
    status: "In Development",
    outcome: "Agentic architecture framework: tool-use, extended thinking, and safety sandboxing for open source LLMs",
    role: "Sole Researcher & Engineer",
    highlights: ["Multi-model routing (Llama, Mistral, DeepSeek)", "nsjail safety sandboxing", "Tool-use architecture"],
    verificationBadges: ["Multi-model", "Safety Sandboxing", "Tool-use Architecture"]
  },
  {
    id: "google-search-ai-platform",
    title: "Google Search AI Developer Platform",
    description: "Built a multi-package monorepo providing AI-powered search capabilities on Google Cloud / Gemini models — REST API backend, Next.js developer portal, CLI tool, JS/Python SDKs, IDE extensions, and Terraform infrastructure-as-code.",
    type: "web",
    year: 2025,
    featured: true,
    category: "Platform Engineering",
    technologies: ["TypeScript", "Express", "Next.js", "React", "Tailwind CSS", "Node.js", "Python", "Zod", "Pino", "Socket.IO", "Commander.js", "Rollup", "Axios", "Monaco Editor", "Recharts", "Swagger UI", "Terraform", "Google Cloud", "Gemini", "Jest"],
    complexity: "Very High",
    impact: "Developer Platform",
    status: "In Development",
    outcome: "Multi-package AI search platform: REST API, developer portal, CLI, JS/Python SDKs, IDE extensions, and IaC on GCP",
    role: "Architect & Sole Engineer",
    highlights: [
      "Monorepo with 7+ packages (API, portal, CLI, SDKs, IDE extensions, IaC)",
      "Express + TypeScript backend with Zod validation and Socket.IO real-time collaboration",
      "Next.js 14 developer portal with Monaco Editor and Swagger API docs",
      "JS & Python SDKs for platform integration",
      "Terraform IaC for multi-region GCP Cloud Run deployment"
    ],
    verificationBadges: ["7+ Packages", "Multi-region GCP", "Gemini-Powered"],
    image: "/assets/images/googlesearch.jpeg"
  },
  {
    id: "pretraining-workbench",
    image: "/assets/images/projects/pretraining-workbench.jpg",
    title: "Pre-Training Workbench — Single-GPU LLM Pre-Training",
    description: "Built a workbench for pre-training transformers from scratch on a single consumer RTX 3080 — ISO-FLOP scaling sweeps, knowledge distillation, and an autoresearch agent that designs and runs its own experiments.",
    type: "research",
    year: 2026,
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "PyTorch", "Transformers", "CUDA", "RTX 3080", "Distillation", "FastAPI"],
    complexity: "Very High",
    impact: "LLM Research Platform",
    status: "In Development",
    outcome: "From-scratch transformer pre-training on a single RTX 3080 with ISO-FLOP scaling sweeps, distillation, and an autonomous research agent",
    role: "Sole Researcher & Engineer",
    highlights: ["From-scratch transformers on consumer GPU", "ISO-FLOP scaling-law sweeps", "Knowledge distillation pipeline", "Autoresearch agent that runs its own experiments"],
    verificationBadges: ["Single-GPU", "ISO-FLOP Sweeps", "Autoresearch Agent"]
  },
  {
    id: "openbell",
    image: "/assets/images/projects/openbell.jpg",
    title: "OpenBell — Opening Bell Briefing (Dell × NVIDIA Hackathon)",
    description: "Built at the Dell × NVIDIA hackathon: a mobile app streaming a live Opening/Closing Bell market briefing, backed by an analysis API and AI video generation running on a local Dell GB10 box with NVIDIA Nemotron.",
    type: "mobile",
    year: 2026,
    featured: true,
    category: "Hackathon",
    technologies: ["Dell GB10", "NVIDIA Nemotron", "Python", "FastAPI", "AI Video Generation", "Mobile", "FinTech"],
    complexity: "High",
    impact: "Hackathon Project",
    status: "Completed",
    outcome: "Live opening/closing bell briefing app with on-device GB10 + Nemotron analysis and AI video generation",
    role: "Engineer",
    highlights: ["Live opening/closing bell video briefings", "On-device GB10 + Nemotron inference", "Analysis & video-generation backend API", "Mobile streaming client"],
    verificationBadges: ["Dell × NVIDIA", "GB10 + Nemotron", "AI Video Generation"]
  },
  {
    id: "executive-coaching-app",
    image: "/assets/images/projects/executive-coaching-app.jpg",
    title: "Executive Coaching App (DeepMind × Vercel Hackathon)",
    description: "Built at the DeepMind × Vercel Zero-to-Agent hackathon in SF: a real-time multimodal AI executive coach for iOS with live scene narration, vision understanding, and streaming inference.",
    type: "mobile",
    year: 2026,
    featured: true,
    category: "Hackathon",
    technologies: ["iOS", "Swift", "Multimodal AI", "Real-time Inference", "Computer Vision", "Live Streaming"],
    complexity: "High",
    impact: "Hackathon Project",
    status: "Completed",
    outcome: "Real-time multimodal AI coach with live scene narration, built at the DeepMind × Vercel Zero-to-Agent hackathon",
    role: "Developer",
    highlights: ["Real-time multimodal AI coaching", "Live scene narration via vision", "Streaming inference on iOS", "Built at DeepMind × Vercel Zero-to-Agent (SF)"],
    verificationBadges: ["DeepMind × Vercel", "Multimodal", "Real-time"]
  },
  {
    id: "ai-voice-tutor",
    image: "/assets/images/projects/ai-voice-tutor.jpg",
    title: "AI Voice Tutor — Canvas LMS Integration",
    description: "Built a voice-driven AI tutor that integrates with the Canvas LMS to answer course questions — pulling assignments and course content to provide conversational, context-aware student support.",
    type: "web",
    year: 2025,
    featured: true,
    category: "Hackathon",
    technologies: ["Python", "Canvas LMS API", "Voice Agents", "LLMs", "Speech-to-Text", "Text-to-Speech"],
    complexity: "High",
    impact: "Hackathon Project",
    status: "Completed",
    outcome: "Voice-driven AI tutor integrated with Canvas LMS for conversational, course-aware student Q&A",
    role: "Engineer",
    highlights: ["Voice-driven conversational tutor", "Canvas LMS course integration", "Context-aware course Q&A"],
    verificationBadges: ["Canvas LMS", "Voice Agent", "Completed"]
  },
  {
    id: "llm-eval-platform",
    image: "/assets/images/projects/llm-eval-platform.jpg",
    title: "LLM Evaluation Platform & Eval Harness",
    description: "Built a multi-model LLM evaluation platform with an automated benchmark harness — LLM-as-judge metrics, classic scores (BERTScore, ROUGE, BLEU), red-teaming, a model catalog, and release scorecards across Ollama and OpenRouter models.",
    type: "web",
    year: 2026,
    featured: true,
    category: "AI/ML",
    technologies: ["Python", "Ollama", "OpenRouter", "LLM-as-Judge", "BERTScore", "ROUGE", "BLEU", "FastAPI"],
    complexity: "Very High",
    impact: "AI Evaluation Platform",
    status: "In Development",
    outcome: "Multi-model LLM eval platform: LLM-judge metrics, BERTScore/ROUGE/BLEU, red-teaming, model catalog, and release scorecards",
    role: "Sole Engineer",
    highlights: ["LLM-as-judge + classic metrics (BERTScore, ROUGE, BLEU)", "Multi-model harness (Ollama, OpenRouter)", "Red-teaming and readiness taxonomy", "Release scorecards and model catalog"],
    verificationBadges: ["LLM-as-Judge", "Red-Teaming", "Multi-Model"]
  },
  {
    id: "vantage-job-agent",
    image: "/assets/images/projects/vantage-job-agent.jpg",
    title: "Vantage — AI Job-Search Agent (StreamIO AI)",
    description: "Built Vantage, an AI job-search agent platform with native macOS and iOS apps — a role pipeline tracker (Applied → Interview → Offer), Google Calendar sync, MCP tool integration, 48K-role pipeline scans, fit-score ranking, and AI-tailored résumé generation.",
    type: "desktop",
    year: 2026,
    featured: true,
    category: "AI/ML",
    technologies: ["AI Agents", "MCP", "Google Calendar API", "Swift", "macOS", "iOS", "TypeScript", "LLM Models"],
    complexity: "Very High",
    impact: "AI Agent Platform",
    status: "In Development",
    outcome: "AI job-search agent with 48K-role pipeline scans, fit-score ranking, Calendar sync, and AI-tailored résumés across macOS and iOS",
    role: "Founder & Lead Engineer",
    highlights: ["Role pipeline tracker (Applied → Interview → Offer)", "48K-role pipeline scan with fit-score ranking", "Google Calendar sync + MCP tool integration", "AI-tailored résumé generation", "Native macOS + iOS apps"],
    verificationBadges: ["AI Agent", "MCP Integration", "macOS + iOS"]
  },
  {
    id: "asana-copilot",
    image: "/assets/images/projects/asana-copilot.jpg",
    title: "Asana Copilot — AI Task Assistant",
    description: "Built an AI copilot for Asana that answers natural-language questions about tasks and projects across mobile and desktop — surfacing status, assignments, and priorities conversationally.",
    type: "web",
    year: 2024,
    featured: false,
    category: "Product",
    technologies: ["TypeScript", "Asana API", "LLMs", "React", "Node.js"],
    complexity: "Medium",
    impact: "Product Demo",
    status: "Completed",
    outcome: "AI copilot for conversational task querying across Asana on mobile and desktop",
    role: "Product Engineer",
    highlights: ["Natural-language task querying", "Mobile + desktop experience", "Asana API integration"],
    verificationBadges: ["AI Copilot", "Asana API", "Completed"]
  },
  {
    id: "intuit-ice-plugin",
    image: "/assets/images/projects/intuit-ice-plugin.jpg",
    title: "Intuit Developer: ICE Plugin",
    description: "Designed and demoed the In-session Customer Engagement (ICE) plugin for the Intuit developer platform — enabling real-time, in-context customer engagement inside partner applications.",
    type: "web",
    year: 2024,
    featured: false,
    category: "Product",
    technologies: ["TypeScript", "Intuit Developer Platform", "JavaScript", "Plugin Architecture"],
    complexity: "Medium",
    impact: "Developer Platform",
    status: "Completed",
    outcome: "In-session Customer Engagement plugin for the Intuit developer platform",
    role: "Staff Product Manager",
    highlights: ["In-session customer engagement", "Intuit developer platform integration", "Partner application embedding"],
    verificationBadges: ["Intuit Platform", "Developer Tooling", "Completed"]
  },
  {
    id: "web-events-controller",
    image: "/assets/images/projects/web-events-controller.jpg",
    title: "Web Events Controller (WSS) — Intuit Application Fabric",
    description: "Built a real-time WebSocket events controller for Intuit's Application Fabric — a pub/sub messaging layer delivering real-time events and updates across distributed developer-platform services.",
    type: "web",
    year: 2023,
    featured: false,
    category: "Platform Engineering",
    technologies: ["WebSocket", "TypeScript", "Node.js", "Pub/Sub", "Intuit Application Fabric", "Event-Driven Architecture"],
    complexity: "High",
    impact: "Platform Infrastructure",
    status: "Completed",
    outcome: "Real-time WebSocket pub/sub events controller for Intuit's Application Fabric",
    role: "Staff Product Manager",
    highlights: ["Real-time WebSocket (WSS) messaging", "Pub/sub event delivery", "Intuit Application Fabric integration"],
    verificationBadges: ["Real-time WSS", "Pub/Sub", "Completed"]
  },
  {
    id: "idme-credentials",
    title: "ID.me Credential Ecosystem Demos",
    description: "Built a suite of 32+ interactive CLI demos covering W3C Verifiable Credentials, decentralized identifiers, zero-knowledge proofs, biometric binding, and compliance frameworks (HIPAA, GDPR).",
    type: "research",
    year: 2025,
    image: "/assets/images/idme.jpeg",
    featured: true,
    category: "Identity & Security",
    technologies: ["Node.js", "JavaScript", "W3C Verifiable Credentials", "DIDs", "Ed25519", "JSON-LD", "ZKP", "Digital Bazaar", "Chalk"],
    complexity: "High",
    impact: "Education Platform",
    status: "Completed",
    outcome: "32+ interactive demos spanning W3C VCs, DIDs, ZKPs, and biometric binding for credential ecosystems",
    role: "Sole Engineer",
    highlights: ["32+ interactive demos", "W3C standards compliance", "Zero-knowledge proofs"],
    verificationBadges: ["32+ Demos", "W3C Standards", "Completed"]
  },
  {
    id: "forensics-cli",
    title: "Forensics Education CLI",
    description: "Built a production-ready CLI with 117 forensics education demos across 15 chapters — featuring fuzzy search, in-window execution, breadcrumb navigation, and Claude Code-inspired interface design.",
    type: "desktop",
    year: 2025,
    image: "/assets/images/forensics.jpeg",
    featured: true,
    category: "Education",
    technologies: ["Python", "Rich", "Click", "thefuzz", "Pytest", "TUI Design"],
    complexity: "Medium",
    impact: "Education Tool",
    status: "Completed",
    outcome: "117 forensics demos across 15 chapters with fuzzy search and Claude Code-inspired TUI",
    role: "Sole Engineer",
    highlights: ["117 interactive demos", "Fuzzy search navigation", "Claude Code-inspired TUI"],
    verificationBadges: ["117 Demos", "15 Chapters", "Completed"]
  },
  {
    id: "streamio-ai",
    title: "StreamIO.AI",
    description: "Built a cross-platform AI content app spanning web, macOS, Windows, and Linux — providing secure, intelligent live digital content with real-time AI-powered insights and native OS integration.",
    type: "desktop",
    year: 2025,
    image: "/assets/images/StreamIO.png",
    link: "https://www.streamio.ai",
    featured: true,
    category: "AI/ML",
    technologies: ["Swift", "macOS", "AI/ML", "Kinde", "TypeScript", "Node.js", "LLM Models", "Web", "Windows", "Linux"],
    complexity: "High",
    impact: "Cross-platform AI Platform",
    status: "In Development",
    outcome: "Cross-platform AI content app with native macOS, Windows, and Linux desktop clients",
    role: "Sole Engineer",
    highlights: ["4-platform support (Web, macOS, Windows, Linux)", "Real-time AI insights", "Native OS integration"],
    verificationBadges: ["4 Platforms", "Native Desktop", "AI-Powered"]
  },
  {
    id: "fintellect-ai",
    title: "FintellectAI",
    description: "Built a mobile-first AI-powered financial education platform — personalized learning paths, AI-guided investment concepts, and adaptive content for beginner through experienced investors.",
    type: "mobile",
    year: 2024,
    image: "/assets/images/app-learn.gif",
    link: "https://www.fintellectai.io",
    featured: true,
    category: "FinTech",
    technologies: ["Python", "Java", "Auth0", "MongoDB", "Kinde", "Stripe", "Next.js", "LLM Models", "iOS", "Android", "AI"],
    complexity: "High",
    impact: "Targeting 10K+ users",
    status: "In Development",
    outcome: "AI-powered financial education platform targeting 10K+ users across iOS and Android",
    role: "Founder & Lead Engineer",
    highlights: ["iOS + Android native apps", "AI-guided learning paths", "Stripe payment integration"],
    verificationBadges: ["iOS + Android", "AI-Powered", "10K+ User Target"]
  },
  {
    id: "nice-air-aviation",
    title: "Nice Air Aviation Flight School",
    description: "Designed and built a comprehensive flight school platform — course management, instructor profiles, aircraft fleet showcase, student resources, and international student support for a complete digital flight training experience.",
    type: "web",
    year: 2023,
    image: "/assets/images/projects/nice-air-aviation.png",
    link: "http://niceairaviation.com",
    featured: true,
    category: "Education",
    technologies: ["HTML5", "CSS3", "JavaScript", "Bootstrap", "jQuery", "Responsive Design", "SEO Optimization", "Content Management"],
    complexity: "Medium",
    impact: "Flight School Platform",
    status: "Completed",
    outcome: "Full-service flight school platform with course management and international student support",
    role: "Sole Engineer",
    highlights: ["Course management system", "SEO optimization", "International student support"],
    verificationBadges: ["Live Production", "SEO Optimized", "Completed"]
  },
  {
    id: "coming-soon",
    title: "MarketX",
    description: "In Development.",
    type: "web",
    year: 2016,
    image: "/assets/images/projects/project-5.png",
    featured: true,
    category: "Development",
    technologies: ["React", "TypeScript", "Node.js"],
    complexity: "Medium",
    impact: "TBD",
    status: "Planning",
    role: "Sole Engineer"
  },
  {
    id: "codename-alpha",
    title: "Codename Alpha: Mobile Application",
    description: "Designed and built a P2P e-commerce mobile app connecting consumers with professional culinary chefs — featuring locale-based search, provider listings, purchase flow, and delivery coordination.",
    type: "mobile",
    year: 2015,
    image: "/assets/images/codenamealphahome.png",
    featured: true,
    category: "E-commerce",
    technologies: ["MySQL", "SQLite", "Java", "Google OAuth API", "Facebook OAuth API"],
    complexity: "High",
    status: "Completed",
    outcome: "P2P culinary marketplace with locale-based search and OAuth-integrated purchase flow",
    role: "Sole Engineer",
    highlights: ["P2P marketplace architecture", "Google + Facebook OAuth", "Locale-based search"],
    verificationBadges: ["P2P Marketplace", "OAuth Integration", "Completed"]
  },
  {
    id: "foodnfellas",
    title: "FoodNFellas: Web Application",
    description: "Built an online marketplace and delivery platform connecting home cooks with local consumers — browse authentic home-cooked meals, place orders, and coordinate delivery from neighborhood kitchens.",
    type: "web",
    year: 2014,
    image: "/assets/images/fnf.png",
    featured: true,
    category: "Marketplace",
    technologies: ["PHP", "Java", "jQuery", "LAMP Stack"],
    complexity: "Medium",
    impact: "1K+ users",
    status: "Completed",
    outcome: "Home-cooked food marketplace reaching 1K+ users with delivery coordination",
    role: "Sole Engineer",
    highlights: ["1K+ active users", "Order + delivery coordination", "LAMP stack at scale"],
    verificationBadges: ["1K+ Users", "Marketplace", "Completed"]
  },
  {
    id: "bmw-watch",
    title: "BMW iPhone Watch App: Mobile Application",
    description: "Designed an Apple Watch interface for vehicle-driver interaction — prototyped human-computer interaction patterns for automotive IoT with purpose-driven design.",
    type: "mobile",
    year: 2014,
    image: "/assets/images/bmwiwatch.png",
    link: "https://www.youtube.com/watch?v=_eQjnLpiQNo",
    featured: false,
    category: "IoT",
    technologies: ["Balsamiq 3", "Pixate", "Apple iWatch UI Kit"],
    complexity: "Medium",
    impact: "Prototype",
    status: "Completed",
    outcome: "Apple Watch automotive interface prototype for BMW vehicle-driver interaction",
    role: "UX Designer",
    highlights: ["Automotive IoT prototype", "Apple Watch UI Kit", "Video demo"],
    verificationBadges: ["Prototype", "Video Demo", "Completed"]
  },
  {
    id: "walmart-forecasting",
    title: "Walmart Sales Revenue Forecasting: Web Application",
    description: "Applied time series and ML methods to predict weekly demand across Walmart store departments — evaluated ensemble learning with clustering and SVR against CPI, holiday, unemployment, and markdown features using WMAE metrics.",
    type: "research",
    year: 2013,
    image: "/assets/images/DAWalmart.png",
    link: "https://github.com/famoruwa/WalmartSalesForecasting/blob/master/Team%206_DataAnalytics_FinalReport.pdf",
    featured: true,
    category: "Data Science",
    technologies: ["R", "SAS", "Weka", "Java", "jQuery", "MySQL"],
    complexity: "High",
    impact: "Research Paper",
    status: "Completed",
    outcome: "Ensemble learning approach outperforming baseline WMAE on Kaggle Walmart sales dataset",
    role: "Lead Researcher",
    highlights: ["Ensemble learning with clustering + SVR", "Kaggle competition dataset", "Published research paper"],
    verificationBadges: ["Research Paper", "Kaggle Dataset", "Completed"]
  },
  {
    id: "cloudsurf",
    title: "CloudSurf: Web Application",
    description: "Built a dynamic pricing plan search engine with real-time mobile and cloud usage analytics, forecasting, and comparison tools for consumer and enterprise plans.",
    type: "web",
    year: 2012,
    image: "/assets/images/devices2.png",
    link: "http://www.cloudsurf.io",
    featured: false,
    category: "Analytics",
    technologies: ["PHP", "SAS", "Java", "jQuery", "MySQL", "Google Maps API", "LAMP Stack"],
    complexity: "High",
    impact: "Enterprise",
    status: "Completed",
    outcome: "Real-time pricing comparison engine with usage analytics and forecasting",
    role: "Sole Engineer",
    highlights: ["Dynamic pricing search", "Usage analytics + forecasting", "Google Maps integration"],
    verificationBadges: ["Enterprise", "Real-time Analytics", "Completed"]
  },
  {
    id: "doe-solar-forecasting",
    title: "Department of Energy: DE-FOA-0000649 (Improving the Accuracy of Solar Forecasting)",
    description: "Delivered a comprehensive energy proposal leveraging Seasonal ARIMA, non-linear regression, and AI heuristics to simulate cloud cover effects on solar panel output — proposing 5 and 10 year regional rollout plans for 100MW photovoltaic grids.",
    longDescription: `With majority of each of the 50 states in the United States having respective RPS (Renewable Portfolio Standards) requirements, our forecasted portfolio projections meet each RPS requirements over a 20 year time horizon from 2009 to 2030. By identifying a forecasted projection of United States energy consumption, the annualized life-time costs associated with constructing and operating photovoltaic grids/panels to meet each state's RPS requirements is depicted as follows: By dividing our project plan timeline for each renewable resource into yearly phases, namely, 1, 2, 5, 10, and 20 year time frames, will assist in promoting a seamless transition and adoption of renewable energy resource photovoltaic grids/panels across all sectors in regions across the United States. Although each state in the United States has its own respective RPS standard, by regionalizing the states within the nation, project planning and implementation across the initially proposed yearly phases will promote a national rollout plan over the 20 year time frame so as to meet the requirements for each region and state respectively on average to meet the national energy goal. Segmenting the solar forecasting process into procedural steps, and leveraging this procedure with a Seasonal ARIMA model, namely the three-step ARMA model process - Identify, Estimate, and Forecast - , will not only provide a seamless and robust method in enhancing solar accuracy, but will also present an opportunity to troubleshoot the process to identify specific errors in the methodology. In the Identify step of the ARMA model, we will calculate different statistics to determine if the series is weakly stationary. Thereafter, we will calculate other statistics to help determine the appropriate ARMA model. For the Estimate step, we will estimate the model's parameters, calculate the different statistics to help judge the model's fit i.e. the AIC and SBC, and finally, calculate different statistics from the residuals to determine if they are approximately white noise. For the final Forecast step, we will use the estimated model to generate the conditional mean for the point forecast, and the estimated variance to generate confidence intervals. In addition, we plan to leverage tooling such as SAS with Business Forecasting for Time Series Modeling, along with non-linear & linear regression modeling, computational algorithmic programming, artificial intelligence heuristics to simulate the effects of cloud cover on intensity of Kw/h retention of solar grids/panels, the effects of seasonality, and multivariate ARIMA models. Based on GHG renewable and non-renewable energy portfolio forecasts that my team has developed previously, we have a forecasted peak cost of photovoltaic output in years 2015, 2018, and 2021, and thus propose 5 and 10 year rollout plans for the implementation of proposed 100MW solar panel grids regionally across the US. We also anticipate the collaboration between NASA, with its SORCE satellite which provides measurements of solar irradiance, and NOAA.gov, with its archive of weather patterns, which will prove useful in developing historical forecasts for future weather forecasts that we anticipate to use in the development of accuracy-enhanced solar forecasts. Additionally, we also plan to develop an operating policy that will align with the solar metrics and the enhanced accuracy of solar forecasts that models the optimal operating conditions with regard to positioning and operating the photovoltaic arrays/grids via a latitudinal and longitudinal coordinate system, which can be simulated via AIMMS/MOPTA modeling. The operating model takes into account that some days may operate at a loss due to foul weather, but overall, we believe that the operating plan offers the best plan of action pertaining to positioning the arrays based on the solar forecasts in order to maximize direct and diffuse solar energy absorption.`,
    type: "consulting",
    year: 2012,
    image: "/assets/images/DOESunshot.png",
    link: "http://www.grants.gov/web/grants/view-opportunity.html?oppId=164854",
    featured: true,
    category: "Energy",
    technologies: ["SAS", "ARIMA", "AIMMS", "MOPTA", "Statistical Modeling"],
    complexity: "Very High",
    impact: "$8M Grant Finalist",
    status: "Completed",
    outcome: "Finalist for $8M US Department of Energy grant — solar forecasting with Seasonal ARIMA and AI heuristics",
    role: "Lead Analyst",
    highlights: ["$8M DOE grant finalist", "Seasonal ARIMA + AI heuristics", "20-year national rollout plan"],
    verificationBadges: ["$8M Grant Finalist", "DOE SunShot", "Completed"]
  },
  {
    id: "doe-seeds",
    title: "Department of Energy: DE-FOA-0000740 (SEEDS - Solar Energy Evolution and Diffusion Studies)",
    description: "Delivered a comprehensive energy proposal for solar adoption research using Six-Sigma DMAIC, ARIMA, KNN, and ANN models to understand diffusion patterns and forecast solar technology adoption across the US.",
    longDescription: `With regards to the conceptual idea of the SEEDS (Solar Energy Evolution and Diffusion Studies) project, our preliminary research in participating in this FOA will involve balancing a targeted Six-Sigma DMAIC approach, with detailed data and quantitative analysis associated with the focal areas chosen. The DMAIC process is used for improving, optimizing and stabilizing business process and designs and can be used as the framework for other improvement applications. We anticipate sharing our quarterly results with the DOE and the SunShot group through regularly scheduled quarterly meetings. The organizational structure entails three groups focusing on Solar Adoption Ideas, Solar Adoption Products, and Solar Adoption Technologies, independently, with cross-collaboration, overseen by the Management team. In each aspect of our focal points for this project, we anticipate providing detailed answers to Innovation Diffusion questions related to how, why, and at what rate each of the focal points spread through society using large data sets and computational tools for analysis. To model, simulate, and forecast the adoption of each of the three focal points once we have gathered the relevant data from each, by leveraging these procedures with a Seasonal ARIMA model, namely the three-step ARMA model process - Identify, Estimate, and Forecast - , KNN (k-nearest neighbors), and ANN (artificial neural network) models, through triangulation, will not only provide a seamless and robust method in understanding the trends that our economy has encountered in the past with respect to Solar Adoption, but will also represent an opportunity to investigate future trends, key drivers and levers which correspond to positive and negative trends in the industry. In addition, we plan to leverage tooling such as simulation software to model market & evolution dynamics, consumer decision-making, social, behavioral and economic barriers and parameters, additional tooling such as SAS with Business Forecasting for Time Series Modeling, along with non-linear & linear regression modeling, computational algorithmic programming, artificial intelligence heuristics to simulate and examine the diffusion challenges, the associated effects of seasonality, and multivariate ARIMA models. For the data management and quantitative analysis component, we plan to establish a client server scenario using J2EE with web service container(s) hosted on a cloud platform with load balancing and failover protection to enable the software as a service platform in managing the large data-sets we anticipate collecting. The client would be developed in multiple flavors on an independent executable interface application. In evaluation of the infrastructure technology, we are proposing Apache Hadoop, which supports data- intensive distributed computing for various computational models, with an Application Server (J2EE), which is an open standard platform that will allow seamless data migrations, and a supporting database.`,
    type: "consulting",
    year: 2012,
    image: "/assets/images/DOESunshot.png",
    link: "http://www.grants.gov/web/grants/view-opportunity.html?oppId=176454",
    featured: false,
    category: "Energy",
    technologies: ["SAS", "ARIMA", "KNN", "ANN", "J2EE", "Hadoop", "Statistical Modeling"],
    complexity: "Very High",
    impact: "Research Grant",
    status: "Completed",
    outcome: "DOE SEEDS proposal: Six-Sigma DMAIC approach to solar adoption diffusion modeling",
    role: "Lead Analyst",
    highlights: ["Six-Sigma DMAIC framework", "ARIMA + KNN + ANN triangulation", "Hadoop data infrastructure"],
    verificationBadges: ["DOE Proposal", "Multi-model", "Completed"]
  },
  {
    id: "toigo-fellowship",
    title: "Toigo Private Equity Fellowship - First Look",
    description: "Selected for Toigo Fellowship and pitched PE acquisition targets (Calpine, Big Lots Inc) to professionals from Apollo Global Management, The Carlyle Group, and Vista Equity Partners at NASDAQ.",
    longDescription: `Pitched PE Acquisition #1 - Calpine:
Calpine is an independent wholesale power company in the United States who owns and operates primarily natural gas-fired and geothermal power plants in North America.

Pitched PE Acquisition #2 - Big Lots Inc:
Big Lots Inc is a a discount retailer for general merchandise and close-out liquidation sales.`,
    type: "consulting",
    year: 2011,
    image: "/assets/images/nasdaq.jpg",
    link: "https://github.com/famoruwa/FA/blob/master/ToigoPitchDeck.pdf",
    featured: false,
    category: "Finance",
    technologies: ["Financial Modeling", "Private Equity", "Investment Analysis"],
    complexity: "High",
    impact: "Fellowship",
    status: "Completed",
    outcome: "Pitched PE acquisitions to Apollo, Carlyle, and Vista Equity Partners at NASDAQ",
    role: "Fellow & Analyst",
    highlights: ["Apollo, Carlyle, Vista EP exposure", "PE acquisition pitch decks", "Toigo Fellowship selection"],
    verificationBadges: ["Toigo Fellowship", "NASDAQ Pitch", "Completed"]
  },
  {
    id: "exxonmobil",
    title: "ExxonMobil & Executive Leadership Council",
    description: "Led a team of 5 through the development of a US energy portfolio projection to 2030 — covering fiscal planning, job growth forecasting, and diversity recruitment strategy, winning a $35,000 scholarship.",
    type: "consulting",
    year: 2011,
    image: "/assets/images/ofacs-team.png",
    link: "http://www.prweb.com/releases/2011/5/prweb8425914.htm",
    featured: false,
    category: "Energy",
    technologies: ["Energy Planning", "Financial Analysis", "HR Forecasting", "Strategic Planning"],
    complexity: "High",
    impact: "$35K Scholarship",
    status: "Completed",
    outcome: "Led team of 5 to $35,000 scholarship — US energy portfolio projection to 2030",
    role: "Team Lead",
    highlights: ["$35K scholarship winner", "Team of 5 leadership", "20-year energy forecast"],
    verificationBadges: ["$35K Scholarship", "Team Lead", "Completed"]
  },
  {
    id: "neural-networks",
    title: "Using Artificial Neural Networks to Predict Secondary Structure of Proteins",
    description: "Designed and trained an artificial neural network on 42 proteins to predict secondary structure from primary sequences — achieving 50%+ prediction accuracy with hand-coded C++ learning algorithms on RHEL.",
    type: "research",
    year: 2005,
    image: "/assets/images/ANN.png",
    link: "https://github.com/famoruwa/ArtificialNeuralNetworks/blob/master/Final%20Research%20Report.pdf",
    featured: true,
    category: "Bioinformatics",
    technologies: ["C++", "Java", "RHEL", "Unit-ProtKB/Swiss-Prot Database", "Neural Networks", "AI"],
    complexity: "High",
    impact: "Research Paper",
    status: "Completed",
    outcome: "Hand-coded C++ neural network achieving 50%+ accuracy on protein structure prediction",
    role: "Lead Researcher",
    highlights: ["Hand-coded C++ neural network", "42-protein training set", "Published research paper"],
    verificationBadges: ["Research Paper", "Hand-coded ANN", "Completed"]
  },
  {
    id: "observability-zero-to-hero",
    title: "Observability Platform",
    description: "Architected a comprehensive real-time observability and automation platform — integrating AI/ML anomaly detection, LLM-powered root cause analysis, distributed tracing, and multi-cloud infrastructure monitoring with Kubernetes orchestration.",
    type: "web",
    year: 2024,
    image: "/assets/images/projects/project-featured.png",
    featured: true,
    category: "Platform Engineering",
    technologies: [
      "AI", "AI Integration", "LLM Integration", "Prompt Engineering", "Anomaly Detection", "Root Cause Analysis", "MLOps", "Model Deployment", "Continuous Learning", "Observability", "Distributed Tracing", "Metrics Collection", "Log Aggregation", "Grafana Dashboards", "Alerting & Automation", "Incident Response", "Kubernetes", "Docker", "Helm", "Terraform", "Ansible", "GitOps", "CI/CD", "Cloud Providers", "Multi-Cloud", "Service Mesh", "Infrastructure as Code", "Platform Engineering", "SRE", "Automation", "Cost Optimization", "RBAC", "OIDC", "Secrets Management", "Encryption", "Audit Logging", "Compliance Reporting", "Data Engineering", "Event-Driven Architecture", "Real-time Streaming", "Kafka", "API/SDK Development", "Performance Tuning", "Reliability Engineering"
    ],
    complexity: "Very High",
    impact: "Enterprise Platform",
    status: "In Development",
    outcome: "Enterprise observability platform with AI-powered anomaly detection and LLM root cause analysis",
    role: "Architect",
    highlights: ["AI anomaly detection + LLM RCA", "Multi-cloud Kubernetes orchestration", "Distributed tracing + Grafana"],
    verificationBadges: ["AI-Powered", "Multi-Cloud", "Enterprise Scale"]
  }
];
